// ============================================================
// Story Quests — narrative, zone-bound quests with per-beat
// triggers and a final unique-item reward. Proof-of-concept
// covers Zone 6 (Ashen Labyrinth); same shape is meant to
// template out to every zone over time.
// ============================================================
/** Authoritative list of the story quests that ship with a fresh save. Loading an existing
 *  save merges any missing entries in (see GameStateManager.load). */
export function createDefaultStoryQuests() {
    // Order is purely the seed order — display-order in the menu derives
    // from `STORY_QUEST_DISPLAY_ORDER` below so the chronological chain
    // stays correct even if a save is migrated mid-update.
    return [
        LABYRINTHS_HEART(), ARCHIVE_BENEATH(), SHADOW_VEIL(), VOIDELF_RITE(),
        TELURIONS_LAIR(), SHADOWREALM_BREACH(), SAGES_PILGRIMAGE(), ETERNAL_PYRE(),
        VAMPIRISM_UNLOCK(), CHAOS_UNLOCK(), BLOODTHIRST_UNLOCK(), HASTE_UNLOCK(),
    ];
}
/** Display-order helper for the story-quest tab. Main chain (top → bottom)
 *  goes first in chronological / prereq order, then the side-quest tail
 *  in tome-line order. Quests not in the array fall back to seed order so
 *  any future addition still renders. */
export const STORY_QUEST_MAIN_ORDER = [
    'shadow_veil', // relic slot 3
    'archive_beneath', // archivist
    'voidelf_rite', // relic slot 4
    'labyrinths_heart', // magmarath
    'telurions_lair', // telurion
    'shadowrealm_breach', // malakhar (stage-50 endboss → endless unlock)
    'eternal_pyre', // inferno unlock
];
export const STORY_QUEST_SIDE_ORDER = [
    'vampirism_unlock', // crimson pact
    'bloodthirst_unlock', // deeper thirst
    'chaos_unlock', // embrace of chaos
    'haste_unlock', // unbroken stride
    'sages_pilgrimage', // rune sage
];
function LABYRINTHS_HEART() {
    const beats = [
        {
            id: 'lh_1_echoes',
            name: 'Echoes of the Labyrinth',
            description: 'Slay 30 minotaurs inside the Ashen Labyrinth.',
            loreText: 'The ash falls like old snow. Each breath tastes of forge-smoke and copper.\n' +
                'Somewhere below, the Labyrinth still beats — slow, patient, hungry.\n' +
                'Cut a path through its children. It will start to notice you.',
            trigger: { kind: 'kill_enemy', faction: 'minotaur', count: 30 },
            progress: 0,
            done: false,
        },
        {
            id: 'lh_2_wardens',
            name: "The Warden's Watch",
            description: 'Defeat 3 Minotaur Wardens.',
            loreText: 'The Wardens walk the inner spiral, each bearing a shard of the old flame.\n' +
                'Break three of them and the path to the heart will open.\n' +
                'They do not fall quietly.',
            trigger: { kind: 'kill_enemy', defId: 'minotaur_warden', count: 3 },
            progress: 0,
            done: false,
        },
        {
            id: 'lh_3_undying',
            name: 'The Undying Flame',
            description: 'Strike down Magmarath the Undying once more.',
            loreText: 'Magmarath has burned since before the first hero drew breath.\n' +
                'Tonight, the Labyrinth will let you name him — and unmake him.\n' +
                'Go. Reach the heart. Take what was hidden there.',
            trigger: { kind: 'kill_boss', defId: 'boss_lava_golem', count: 1 },
            progress: 0,
            done: false,
        },
    ];
    return {
        id: 'labyrinths_heart',
        name: "The Labyrinth's Heart",
        zone: 'Ashen Labyrinth',
        minStage: 38,
        beats,
        currentBeat: 0,
        claimed: false,
        // Chained behind voidelf_rite per user main-chain ordering — Magmarath
        // sits between Relic Slot 4 and Telurion in the chronological story.
        requiresQuestId: 'voidelf_rite',
    };
}
function ARCHIVE_BENEATH() {
    const beats = [
        {
            id: 'ab_1_whispers',
            name: 'Whispers in the Crypts',
            description: 'Descend into the Haunted Crypts.',
            loreText: 'The Crypts remember every name. Below the tombs, an older voice is scratching\n' +
                'letters into bone. The Archivist has been counting the years since anyone came.\n' +
                'Tonight, someone does.',
            trigger: { kind: 'enter_zone', zoneName: 'Haunted Crypts' },
            progress: 0,
            done: false,
        },
        {
            id: 'ab_2_dust',
            name: 'Clear the Dust',
            description: 'Put 40 undead back in their graves.',
            loreText: 'The Archive is sealed by every bone you leave standing. Thin the chorus and\n' +
                'the locks will loosen. Forty should be enough to be heard.',
            trigger: { kind: 'kill_enemy', faction: 'undead', count: 40 },
            progress: 0,
            done: false,
        },
        {
            id: 'ab_3_archivist',
            name: 'The Archivist, Bound in Bone',
            description: 'Strike down the Archivist and take the lost tome.',
            loreText: 'He was a scholar once, patient as stone. Now the tome keeps him stitched to\n' +
                'the Crypts. Break the lich and the Archive will open.\n' +
                'Go. The book is waiting for its next reader.',
            trigger: { kind: 'kill_boss', defId: 'boss_skeleton_mage', count: 1 },
            progress: 0,
            done: false,
        },
    ];
    return {
        id: 'archive_beneath',
        name: 'The Archive Beneath',
        zone: 'Haunted Crypts',
        minStage: 3,
        beats,
        currentBeat: 0,
        claimed: false,
        // Chained behind shadow_veil (Veil of the Shadow Realm — Relic Slot 3 unlock).
        // Reasoning: the Archivist is a frost-school caster boss whose Frost Nova room-
        // wipe punishes squishy parties; players who have already cleared Shadow Realm
        // have demonstrated they can handle a tier-2 boss. Until shadow_veil is claimed,
        // computeStoryBossGating treats `boss_skeleton_mage` as locked and the Archivist
        // never spawns on Haunted Crypts boss waves. Earlier history: this quest was
        // briefly chained to labyrinths_heart (stage 38), which gated the Archivist too
        // late and most players never saw him; shadow_veil (stage 9+) is the right tier.
        requiresQuestId: 'shadow_veil',
    };
}
function SHADOW_VEIL() {
    const beats = [
        {
            id: 'sv_1_enter',
            name: 'Into the Shadow Realm',
            description: 'Pierce the veil and enter the Shadow Realm.',
            loreText: 'The sky here has forgotten its stars.\n' +
                'Only the shadow remembers, and it remembers hungrily.\n' +
                'Step through. Do not look back.',
            trigger: { kind: 'enter_zone', zoneName: 'Shadow Realm' },
            progress: 0,
            done: false,
        },
        {
            id: 'sv_2_legion',
            name: 'Thin the Legion',
            description: 'Put down 80 demons beyond the veil.',
            loreText: 'The veil is held shut by the weight of the legion.\n' +
                'Cut enough of them loose and it will begin to thin.\n' +
                'The void behind it has been waiting a long time.',
            trigger: { kind: 'kill_enemy', faction: 'demon', count: 80 },
            progress: 0,
            done: false,
        },
        {
            id: 'sv_3_warlord',
            name: 'The Abyssal Warlord',
            description: 'Slay the Abyssal Demon beyond the veil. Rewards relic slot 3.',
            loreText: 'His crown is shadow. His blade is older than any kingdom that still stands.\n' +
                'Break him, and the veil will break with him — the third relic binding will\n' +
                'open to the hand that carried his heart back.',
            trigger: { kind: 'kill_boss', defId: 'boss_demon1', count: 1 },
            progress: 0,
            done: false,
        },
    ];
    return {
        id: 'shadow_veil',
        name: 'Veil of the Shadow Realm',
        zone: 'Shadow Realm',
        minStage: 9,
        beats,
        currentBeat: 0,
        claimed: false,
    };
}
function VOIDELF_RITE() {
    const beats = [
        {
            id: 'vr_1_enter',
            name: 'Into the Demon Rift',
            description: 'Step through the rift.',
            loreText: 'The rift hums with dragonsong and the low chant of voidcallers.\n' +
                'Pull the veil aside; the ones on the other side are already waiting.',
            trigger: { kind: 'enter_zone', zoneName: 'Demon Rift' },
            progress: 0,
            done: false,
        },
        {
            id: 'vr_2_dragons',
            name: 'The Scales Answer',
            description: 'Slay 30 dragons in the Rift.',
            loreText: 'Every dragon here is a seal the Void Elves paid for in centuries.\n' +
                'Cut enough of them loose and the voidwalker\'s chains will slacken too.',
            trigger: { kind: 'kill_enemy', faction: 'dragon', count: 30 },
            progress: 0,
            done: false,
        },
        {
            id: 'vr_3_voidelf',
            name: 'The Void Elf Warlord',
            description: 'Defeat the Void Elf Warlord. Rewards relic slot 4.',
            loreText: 'His lance opens holes where no light has ever walked.\n' +
                'Break him and the fourth relic binding will open to the hand that\n' +
                'carried his crown back through the rift.',
            trigger: { kind: 'kill_boss', defId: 'boss_voidelf', count: 1 },
            progress: 0,
            done: false,
        },
    ];
    return {
        id: 'voidelf_rite',
        name: 'Rite of the Void Elves',
        zone: 'Demon Rift',
        minStage: 30,
        beats,
        currentBeat: 0,
        claimed: false,
        // Chained behind archive_beneath per user main-chain ordering — Relic
        // Slot 4 is the next progression step after the Archivist falls.
        requiresQuestId: 'archive_beneath',
    };
}
function TELURIONS_LAIR() {
    const beats = [
        {
            id: 'tl_1_storm',
            name: 'Stormwatch',
            description: 'Defeat 5 Sentinels of Telurion in the Lair.',
            loreText: 'The dragon\'s wardens stand on the high stones, lit by old lightning.\n' +
                'They bear his colours and his patience. Five must fall before he\n' +
                'will look you in the eye.',
            trigger: { kind: 'kill_enemy', defId: 'skeleton_elite_gold', count: 5 },
            progress: 0,
            done: false,
        },
        {
            id: 'tl_2_telurion',
            name: "Telurion, Stormcoiled",
            description: "Strike down Telurion in his own lair.",
            loreText: 'The mountain hums when he wakes. His coil writes the storm into stone\n' +
                'and the sky leans down to listen. Break him, and the charm at his\n' +
                'heart will be yours.',
            trigger: { kind: 'kill_boss', defId: 'boss_telurion', count: 1 },
            progress: 0,
            done: false,
        },
    ];
    return {
        id: 'telurions_lair',
        name: "Telurion's Lair",
        zone: "Telurion's Lair",
        // Bumped 30 → 40 per user main-chain re-ordering: Telurion now sits at
        // the end of the chain (after Magmarath at stage 38), so its minStage
        // matches the post-Magmarath gate.
        minStage: 40,
        beats,
        currentBeat: 0,
        claimed: false,
        // Chained behind labyrinths_heart so the main story flows
        // shadow_veil → archive_beneath → voidelf_rite → labyrinths_heart →
        // telurions_lair → eternal_pyre.
        requiresQuestId: 'labyrinths_heart',
    };
}
/** "Shadowrealm Breach" — stage-50 endboss + endless-mode unlock. Locked
 *  behind Telurion's death (his fall is what cracks the veil), then asks
 *  the player to push to stage 50 and put down Malakhar himself. The
 *  Shadowrealm Sigil drops on first-kill via BattleScene.grantShadowlordReward;
 *  the random-roll pool gates the sigil on this quest's claim. */
function SHADOWREALM_BREACH() {
    const beats = [
        {
            id: 'sb_1_threshold',
            name: 'The Crack in the World',
            description: 'Reach stage 50 with the veil already broken.',
            loreText: "Telurion's death did not close the door — it tore it.\n" +
                'Far below the burning labyrinth, far past the storms, the\n' +
                'Shadowrealm leans against its own bars. Walk to the fiftieth\n' +
                'stage. The bars are already bending.',
            trigger: { kind: 'reach_stage', stage: 50 },
            progress: 0,
            done: false,
        },
        {
            id: 'sb_2_hollow_king',
            name: 'Malakhar, the Hollow King',
            description: 'Kill Malakhar before the portal opens.',
            loreText: 'He is not a god. He is what is left when reality flinches —\n' +
                'a king of nothing, crowned in absence. He has been the warden\n' +
                'and the prisoner of the same wound since before the first\n' +
                'kingdom was named. Put him down. The portal will open behind\n' +
                'his collapse, and the dead will pour through forever.',
            trigger: { kind: 'kill_boss', defId: 'boss_shadowlord', count: 1 },
            progress: 0,
            done: false,
        },
    ];
    return {
        id: 'shadowrealm_breach',
        name: 'Shadowrealm Breach',
        zone: 'The Shadowrealm',
        minStage: 50,
        beats,
        currentBeat: 0,
        claimed: false,
        // Telurion's fall is what makes Malakhar reachable — without it the
        // veil holds and stage 50 is just another wave of normal content.
        requiresQuestId: 'telurions_lair',
    };
}
/** "The Eternal Pyre" — unlock for Inferno Mode. The capstone of the main
 *  story chain: once Telurion falls, the Pyre opens and Inferno saves
 *  become available across every save slot on the account. Quest-claim
 *  is what flips `account.infernoUnlocked` to true. */
function ETERNAL_PYRE() {
    const beats = [
        {
            id: 'ep_1_threshold',
            name: 'Threshold of the Pyre',
            description: 'Carry your war to stage 40.',
            loreText: 'The Pyre has burned since before the first crown was struck.\n' +
                'It does not warm — it judges. Reach stage forty and step\n' +
                'into its light. What it shows you is yours to bear.',
            trigger: { kind: 'reach_stage', stage: 40 },
            progress: 0,
            done: false,
        },
    ];
    return {
        id: 'eternal_pyre',
        name: 'The Eternal Pyre',
        zone: 'Any zone',
        minStage: 40,
        beats,
        currentBeat: 0,
        claimed: false,
        requiresQuestId: 'telurions_lair',
    };
}
/** "The Sage's Pilgrimage" — unlocks the Rune Sage NPC. Account-level claim:
 *  once any save slot finishes the quest, the Sage spawns in every run for
 *  that account (mirrors the relic-slot quest pattern via account-wide claim
 *  mirroring in GameStateManager). Three beats: prove you've reached
 *  mid-game, that you can sustain a fight, and that you can close out a zone. */
function SAGES_PILGRIMAGE() {
    // Bumped to endgame difficulty per user feedback ("supposed to be one of
    // the end-game features"). Each beat scaled up so the quest reads as a
    // capstone, not an early-run errand: stage 30 (was 15), 300 kills (was
    // 80), 8 zone bosses (was 2), and a new 4th beat that requires a stage-
    // 30+ boss kill so the player has to actually push past the mid-game
    // wall to finish it.
    const beats = [
        {
            id: 'sp_1_path',
            name: "The Pilgrim's Path",
            description: 'Reach stage 30.',
            loreText: 'The runes hum below the world, but they only sing for those who walk far enough to hear.\n' +
                'Carry your war deep — past the second wall, past the third. Reach stage thirty,\n' +
                'and the Sage will know your name. She has been waiting longer than empires.',
            trigger: { kind: 'reach_stage', stage: 30 },
            progress: 0,
            done: false,
        },
        {
            id: 'sp_2_toll',
            name: "Wisdom's Toll",
            description: 'Slay 300 foes on your path.',
            loreText: 'Every fall feeds the runes a little — old blood for old power.\n' +
                'Three hundred foes, no fewer. The Sage counts them by the cooling of the earth.\n' +
                'She does not bargain with cowards or with those who flinch at the work.',
            trigger: { kind: 'kill_enemy', count: 300 },
            progress: 0,
            done: false,
        },
        {
            id: 'sp_3_echoes',
            name: 'Echoes of Power',
            description: 'Defeat 8 zone bosses.',
            loreText: 'A boss falls and the world tilts an inch — that is what the runes feed on.\n' +
                'Eight of them. Any eight. The Sage does not care whose crown you crack —\n' +
                'only that your hands are heavy with their dust before you stand before her.',
            trigger: { kind: 'kill_boss', count: 8 },
            progress: 0,
            done: false,
        },
        {
            id: 'sp_4_apex',
            name: 'The Apex Hunt',
            description: 'Bring down a stage-30+ champion.',
            loreText: 'The Sage will not bargain with a tourist. Push your war past the thirtieth zone\n' +
                'and break a champion at the apex of the climb. The runes only finish their\n' +
                'song when the sky has heard the apex fall.',
            trigger: { kind: 'reach_stage', stage: 35 },
            progress: 0,
            done: false,
        },
    ];
    return {
        id: 'sages_pilgrimage',
        name: "The Sage's Pilgrimage",
        // Zone-agnostic — the quest spans the run, not a single biome. The display shows
        // "Any zone" but the matcher doesn't filter on zoneName for this quest.
        zone: 'Any zone',
        minStage: 1,
        beats,
        currentBeat: 0,
        claimed: false,
    };
}
/** "The Crimson Pact" — unlocks the Bloody Book (lifesteal) tome. Single-beat undead
 *  kill grind that any save can complete just by playing through Crypts content (or any
 *  zone with skeletons). Account-claimed, so once any slot finishes it the tome stays in
 *  the random pool for future heroes. */
function VAMPIRISM_UNLOCK() {
    const beats = [
        {
            id: 'vu_1_drink',
            name: 'The Crimson Pact',
            description: 'Slay 200 undead. Their unlife will fuel your own.',
            loreText: 'The bones do not die — they only forget how to bleed.\n' +
                'Cut two hundred of them down and the silence underneath will speak.\n' +
                'It will offer your blade a thirst.',
            trigger: { kind: 'kill_enemy', faction: 'undead', count: 200 },
            progress: 0,
            done: false,
        },
    ];
    return {
        id: 'vampirism_unlock',
        name: 'The Crimson Pact',
        zone: 'Any zone',
        minStage: 1,
        beats,
        currentBeat: 0,
        claimed: false,
    };
}
/** "Embrace of Chaos" — unlocks the Book of Chaos (random stat boost) tome. Demon
 *  kill grind, zone-agnostic. */
function CHAOS_UNLOCK() {
    const beats = [
        {
            id: 'cu_1_break',
            name: 'Embrace of Chaos',
            description: 'Slay 200 demons. Order is a cage; let it break.',
            loreText: 'Two hundred demons, two hundred unmade laws.\n' +
                'Each one you cut down loosens another seam in the world.\n' +
                'When the last falls, chaos will recognise you as one of its own.',
            trigger: { kind: 'kill_enemy', faction: 'demon', count: 200 },
            progress: 0,
            done: false,
        },
    ];
    return {
        id: 'chaos_unlock',
        name: 'Embrace of Chaos',
        zone: 'Any zone',
        minStage: 1,
        beats,
        currentBeat: 0,
        claimed: false,
        // Chained behind bloodthirst_unlock per user — chaos is the
        // capstone of the side tome line (vampirism → bloodthirst → chaos).
        requiresQuestId: 'bloodthirst_unlock',
    };
}
/** "The Deeper Thirst" — unlocks the Book of Bloodthirst (max-HP-per-kill) tome.
 *  Requires `vampirism_unlock` first — the player must already drink before they can
 *  hunger. Three hundred undead kills past that point. */
function BLOODTHIRST_UNLOCK() {
    const beats = [
        {
            id: 'bu_1_hunger',
            name: 'The Deeper Thirst',
            description: 'With the pact sealed, slay 300 more undead.',
            loreText: 'The pact is signed, but a thirst untaught is a thirst unfed.\n' +
                'Three hundred more bones must crack before the hunger learns its full shape.\n' +
                'Then the dead will mend you as you mend them apart.',
            trigger: { kind: 'kill_enemy', faction: 'undead', count: 300 },
            progress: 0,
            done: false,
        },
    ];
    return {
        id: 'bloodthirst_unlock',
        name: 'The Deeper Thirst',
        zone: 'Any zone',
        minStage: 1,
        beats,
        currentBeat: 0,
        claimed: false,
        requiresQuestId: 'vampirism_unlock',
    };
}
/** "Unbroken Stride" — unlocks the Book of Haste (cooldown reduction) tome.
 *  Player must reach stage 20 in a single clean run, no Stay button presses.
 *  Staying even once disqualifies the run; the player has to die / reset and
 *  try again with a fresh stayCount. */
function HASTE_UNLOCK() {
    const beats = [
        {
            id: 'hu_1_stride',
            name: 'Unbroken Stride',
            description: 'Reach stage 20 in one breath — never use Stay.',
            loreText: 'Time is a curse you outrun by never looking back.\n' +
                'Twenty stages forward. No retreats, no rest, no second chances.\n' +
                'The runes of haste belong to those who refuse to slow.',
            trigger: { kind: 'reach_stage_no_stay', stage: 20 },
            progress: 0,
            done: false,
        },
    ];
    return {
        id: 'haste_unlock',
        name: 'Unbroken Stride',
        zone: 'Any zone',
        minStage: 1,
        beats,
        currentBeat: 0,
        claimed: false,
    };
}
/** True if a quest is unlocked — its prerequisite (if any) has been claimed.
 *
 *  TEST MODE: prereq chain disabled — every quest is treated as unlocked from
 *  the start so all bosses can be tested in parallel without first grinding
 *  through their predecessors (e.g. Shadowrealm Breach without first claiming
 *  Telurion's Lair, Magmarath without first claiming The Archive Beneath).
 *  Restore the chain by re-enabling the body below when the questline is
 *  ready for player-facing release.
 */
export function isStoryQuestUnlocked(_quest, _all) {
    return true;
    // if (!_quest.requiresQuestId) return true;
    // const prereq = _all.find((q) => q.id === _quest.requiresQuestId);
    // return prereq?.claimed === true;
}
/** Advance story-quest state in-place. Returns a summary of what changed so the UI can
 *  react. Idempotent: calling twice with the same event won't re-trigger lore. */
export function advanceStoryQuests(quests, event) {
    const result = { started: [], completed: [], finished: [] };
    for (const quest of quests) {
        if (quest.claimed)
            continue;
        if (event.stage < quest.minStage)
            continue;
        if (quest.currentBeat >= quest.beats.length)
            continue;
        // Chained quest — wait until prerequisite is done before processing any events.
        if (!isStoryQuestUnlocked(quest, quests))
            continue;
        const beat = quest.beats[quest.currentBeat];
        if (beat.done)
            continue;
        // Enter-zone beats auto-complete on first entry — walking INTO the zone IS the
        // beat's completion. Previously progress was bumped to 0.0001 but `done` was never
        // flipped, so a quest whose first beat was an `enter_zone` trigger stayed stuck on
        // beat 0 indefinitely and the kill-objective beat below it never activated. The
        // "started" popup still fires for the opening lore; "completed" fires so the UI
        // acknowledges the beat, and the NEXT beat's opening lore is queued immediately.
        if (beat.trigger.kind === 'enter_zone'
            && event.kind === 'enter_zone'
            && event.zoneName === beat.trigger.zoneName) {
            beat.progress = 1;
            beat.done = true;
            result.started.push({ quest, beat });
            result.completed.push({ quest, beat });
            quest.currentBeat++;
            if (quest.currentBeat >= quest.beats.length) {
                result.finished.push(quest);
            }
            else {
                const nextBeat = quest.beats[quest.currentBeat];
                result.started.push({ quest, beat: nextBeat });
            }
            continue;
        }
        // Kill-based beats.
        const t = beat.trigger;
        let progressed = false;
        if (t.kind === 'kill_enemy' && event.kind === 'kill_enemy') {
            const factionOk = !t.faction || t.faction === event.faction;
            const defOk = !t.defId || t.defId === event.defId;
            if (factionOk && defOk) {
                beat.progress = Math.min(t.count, Math.floor(beat.progress) + 1);
                progressed = true;
            }
        }
        else if (t.kind === 'kill_boss' && event.kind === 'kill_boss') {
            // defId is optional — if omitted, ANY boss kill counts (useful for
            // "defeat 2 zone bosses" style beats that don't care which one).
            if (!t.defId || t.defId === event.defId) {
                beat.progress = Math.min(t.count, Math.floor(beat.progress) + 1);
                progressed = true;
            }
        }
        else if (t.kind === 'reach_stage' && event.kind === 'advance_stage') {
            // Auto-completes once the player advances to a stage at or past the
            // target. One-shot — fire `started` + `completed` together.
            if (event.newStage >= t.stage) {
                beat.progress = 1;
                beat.done = true;
                result.started.push({ quest, beat });
                result.completed.push({ quest, beat });
                quest.currentBeat++;
                if (quest.currentBeat >= quest.beats.length) {
                    result.finished.push(quest);
                }
                else {
                    const nextBeat = quest.beats[quest.currentBeat];
                    result.started.push({ quest, beat: nextBeat });
                }
                continue;
            }
        }
        else if (t.kind === 'reach_stage_no_stay' && event.kind === 'advance_stage') {
            // Same as reach_stage but requires a clean run — stayCount must be 0.
            // If the player has stayed at all, skip; the beat simply doesn't fire
            // and the player has to start a fresh run to get another shot.
            if (event.newStage >= t.stage && (event.stayCount ?? 0) === 0) {
                beat.progress = 1;
                beat.done = true;
                result.started.push({ quest, beat });
                result.completed.push({ quest, beat });
                quest.currentBeat++;
                if (quest.currentBeat >= quest.beats.length) {
                    result.finished.push(quest);
                }
                else {
                    const nextBeat = quest.beats[quest.currentBeat];
                    result.started.push({ quest, beat: nextBeat });
                }
                continue;
            }
        }
        if (progressed) {
            const neededCount = (t.kind === 'enter_zone' || t.kind === 'reach_stage' || t.kind === 'reach_stage_no_stay') ? 1 : t.count;
            if (beat.progress >= neededCount) {
                beat.done = true;
                result.completed.push({ quest, beat });
                quest.currentBeat++;
                if (quest.currentBeat >= quest.beats.length) {
                    result.finished.push(quest);
                }
                else {
                    // Auto-announce the NEXT beat's lore now that it's the active one. The UI queues
                    // these popups so they don't stomp each other.
                    const nextBeat = quest.beats[quest.currentBeat];
                    result.started.push({ quest, beat: nextBeat });
                }
            }
        }
    }
    return result;
}
/** Count of completed (done) beats across all story quests — useful for a HUD badge. */
export function countCompletedStoryBeats(quests) {
    let n = 0;
    for (const q of quests)
        for (const b of q.beats)
            if (b.done)
                n++;
    return n;
}
//# sourceMappingURL=storyQuests.js.map